import {
	DATA_LOADED,
	DATA_REQUESTED,
	DATA_UPDATED,
	ID_NOT_SET,
	AUTHENTICATION_KEY,
	REPLICA_LIST, REPLICALIST_REPLICA,
	REPLICALIST_REPLICALIST,
	REPLICALIST_STOREDLIST,
} from "../../_MODULE_GLOBALS/constants";
import {storeInCache} from "../../../utils/manageStoreCacheData";
import {createNewStoreState} from "./generalReducerActions";
import {isEmpty, isTrue} from "../../../utils/generalUtils";

/*
   This reducer fetches and updates replica and replica list attributes.
   Note: replica list is returned as an array of replica page objects as "pages"
 */

/**
 * List of potential replica and replicaList store properties for a module that
 * implements replica page and replica list display.
 *
 * This attempts to ensure that there is a placeholder in the store for
 * the values returned for the module.
 */
const createInitialState = ((storageKey) => {
	return {
		[storageKey]: {
			"page": '',
			"pageNumber": '',
			"folio": '',
			"replicaList": [],
			"listStyleToc": '',
			"issueUrl": '',

			"pageSpread": '',
			"zoomLevel": '',

			"documentId": '',
			"documentTitle": '',
			"documentUrl": '',
			"pdfDownloadUrl": '',

			"fetchListParams": {},
			"fetchQueryParams": null,
			"navigationAttributes": {},
			"fetchInProgress": false,

			"forceFetch": false,
			"replaceHistoryState": false,
			"updateHistoryState": true,

			"articleList": [],
			"articleId": ID_NOT_SET
		}
	};
});

/**
 * List of attributes that should be re-initialized each time a reducer gets called.
 *
 * @type {function(*): {}}
 */
const resetStoreState = ((storageKey) => {
	return {
		[storageKey]: {
			"replaceHistoryState": false,
			"updateHistoryState": true,
			"forceStoreUpdate": false,
		}
	};
});


/**
 * Derive per-issue accessPages from replica page data and write them into the
 * AUTHENTICATION store. Restricted issues get the list of accessible folios;
 * full-access issues get an empty array. Mutates returnState in place.
 *
 * @param returnState the store state being built by the reducer
 * @param replicaList array of replica page objects (each with issueUrl, folio, userHasAccess)
 */
const applyAccessPagesFromReplicaList = (returnState, replicaList) => {
	if (isEmpty(replicaList) || replicaList.length === 0) {
		return;
	}
	const issueUrl = replicaList[0].issueUrl;
	if (isEmpty(issueUrl)) {
		return;
	}
	const accessibleFolios = replicaList
		.filter(page => isTrue(page.userHasAccess))
		.map(page => page.folio);
	const hasRestrictions = accessibleFolios.length < replicaList.length;
	// The issue is accessible (in the replica viewer) when at least one page is accessible:
	//   full access -> every page accessible, no restrictions
	//   lookinside   -> pages 1-5 accessible, restricted
	//   no access    -> zero accessible pages
	const hasAnyAccess = accessibleFolios.length > 0;
	const authStorageKey = AUTHENTICATION_KEY;
	if (!returnState[authStorageKey]) {
		returnState[authStorageKey] = {};
	}
	if (!returnState[authStorageKey].userAccess) {
		returnState[authStorageKey].userAccess = { isLoggedIn: true, access: {}, accessPages: {} };
	}
	// Write BOTH access and accessPages for this issue, derived from the replica page data itself.
	// This runs on every replica load AND on the cached re-entry path, so it re-asserts the issue's
	// access each time the user enters the issue — unlike the one-shot globals loop-stop, which only
	// fires on a fresh lookinside auto-login. Without access being (re)written here, navigating out
	// of a lookinside-preview issue and back left access[issueUrl] false (or reset to false by an
	// article-list call resolved to the login id), so the popup showed on every page on re-entry.
	//   access      -> true when any page is accessible (full OR page-limited preview)
	//   accessPages -> the accessible folio list when restricted, empty array when full access
	returnState[authStorageKey].userAccess = {
		...returnState[authStorageKey].userAccess,
		access: { ...returnState[authStorageKey].userAccess.access, [issueUrl]: hasAnyAccess },
		accessPages: { ...returnState[authStorageKey].userAccess.accessPages, [issueUrl]: hasRestrictions ? accessibleFolios : [] }
	};
};


function replicaReducers(state, action) {
	// check if module action call matches REPLICA_LIST; if not present, set default to REPLICA_LIST
	const moduleAction = (action.params && action.params.type) ? action.params.type : REPLICA_LIST;

	if (moduleAction === REPLICA_LIST && action.type === DATA_REQUESTED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ReplicaList';

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});

		returnState[storageKey].fetchInProgress = true;

		return returnState;
	}

	if (moduleAction === REPLICA_LIST && action.type === DATA_LOADED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ReplicaList';

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});

		// handle replicaList separately, as the return value is a list of pages
		const replicaList = Array.isArray(action.payload.pages) ? action.payload.pages : (!isEmpty(returnState[storageKey].replicaList) ? returnState[storageKey].replicaList : []);
		returnState[storageKey].replicaList = replicaList;
		// remove pages to keep size of returnState down
		delete(returnState[storageKey].pages);

		// cache return replicaList with fetchQueryParams object as the key
		storeInCache({dataKey: returnState[storageKey].fetchQueryParams, keyType: 'replicaPages', data: replicaList, overrideEmptyData: true, state: returnState});

		const listStyleToc = action.payload.listStyleToc;
		returnState[storageKey].listStyleToc = listStyleToc;
		storeInCache({dataKey: returnState[storageKey].fetchQueryParams, keyType: 'listStyleToc', data: listStyleToc, overrideEmptyData: true, state: returnState});
		
		returnState[storageKey].fetchInProgress = false;

		// Derive accessPages from replica page data and update authentication store
		// This provides the page-level access info for the authentication popup
		applyAccessPagesFromReplicaList(returnState, replicaList);

		return returnState;
	}

	if (moduleAction === REPLICALIST_STOREDLIST && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ReplicaList';

		// create new store state and update only changed attributes
		const returnState = createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});

		const replicaList = Array.isArray(action.payload.replicaList) ? action.payload.replicaList : (!isEmpty(returnState[storageKey].replicaList) ? returnState[storageKey].replicaList : []);
		returnState[storageKey].replicaList = replicaList;
		// remove pages to keep size of returnState down
		delete(returnState[storageKey].pages);

		// Derive accessPages from cached replica page data (same as DATA_LOADED)
		applyAccessPagesFromReplicaList(returnState, replicaList);

		return returnState;
	}

	// Action called through clicking on replica page from replica list
	if (moduleAction === REPLICALIST_REPLICALIST && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'ReplicaList';

		// create new store state and update only changed attributes
		return createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});
	}

	if (moduleAction === REPLICALIST_REPLICA && action.type === DATA_UPDATED) {
		const storageKey = (action.params && action.params.storageKey) ? action.params.storageKey : 'replicaViewer';

		// create new store state and update only changed attributes
		return createNewStoreState({
			storageKey: storageKey,
			state: state,
			action: action,
			initialStorageState: createInitialState(storageKey),
			resetStorageStateValues: resetStoreState(storageKey)
		});
	}

	// if no matches, just return original state
	return state;
}
export default replicaReducers;
